// ═══════════════════════════════════════════════════════════════
// ChapterHeader — En-tête chapitre dans ChapterEditor (S56 P3)
// Titre éditable, statut dropdown, word count, boutons actions.
// Boutons Consolider (S59) / Export (S59) livrés en stub.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef } from 'react';
import { Check, Clock, Sparkles, CircleDot, Archive as ArchiveIcon, Download as DownloadIcon, History, Wand2, ChevronDown, FileText, FileType2, File, ArrowLeftRight, Zap } from 'lucide-react';

const STATUS_META = {
    draft:        { label: 'Brouillon',   color: 'var(--text-muted)', dot: '#6a6a85' },
    in_progress:  { label: 'En cours',    color: '#f59e0b', dot: '#f59e0b' },
    review:       { label: 'Revue',       color: '#3b82f6', dot: '#3b82f6' },
    validated:    { label: 'Validé',      color: '#10b981', dot: '#10b981' },
    consolidated: { label: 'Consolidé',   color: '#8b5cf6', dot: '#8b5cf6' },
};

const SAVE_STATES = {
    idle:    { label: '', color: 'transparent' },
    dirty:   { label: 'Modifié…', color: '#f59e0b' },
    saving:  { label: 'Enregistrement…', color: '#3b82f6' },
    saved:   { label: 'Sauvegardé', color: '#10b981' },
    error:   { label: 'Erreur sauvegarde', color: '#ef4444' },
};

const ChapterHeader = ({
    bookId,
    chapter,
    saveState = 'idle',
    lastSavedAt = null,
    versionCount = 0,
    onRenameChapter,
    onStatusChange,
    onOpenVersions,
    onRevise,
    onConsolidate,
    onOpenTransitionToNext,
    hasNextChapter = false,
    onToggleNotes,
    notesCount = 0,
    notesOpen = false,
}) => {
    const [editingTitle, setEditingTitle] = useState(false);
    const [titleDraft, setTitleDraft] = useState(chapter?.title || '');
    const [showStatusMenu, setShowStatusMenu] = useState(false);
    const [showExportMenu, setShowExportMenu] = useState(false);
    const [exportSubmenu, setExportSubmenu] = useState(null); // 'docx' | 'pdf' | null
    const [tick, setTick] = useState(0);
    const menuRef = useRef(null);
    const exportMenuRef = useRef(null);

    const exportChapter = (format, palette = null) => {
        const token = localStorage.getItem('oracle_token');
        const qs = new URLSearchParams({ format });
        if (palette) qs.set('palette', palette);
        fetch(`/api/books/${bookId}/chapters/${chapter.id}/export?${qs.toString()}`, {
            headers: { 'Authorization': `Bearer ${token}` },
        }).then(async (res) => {
            if (!res.ok) {
                const e = await res.json().catch(() => ({}));
                alert(`Erreur export: ${e.error || res.statusText}`);
                return;
            }
            const blob = await res.blob();
            const cd = res.headers.get('Content-Disposition') || '';
            const match = cd.match(/filename="([^"]+)"/);
            const filename = match ? match[1] : `chapter.${format}`;
            const dlUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = dlUrl; a.download = filename;
            document.body.appendChild(a); a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(dlUrl);
        }).catch(e => alert(`Erreur export: ${e.message}`));
        setShowExportMenu(false);
        setExportSubmenu(null);
    };

    useEffect(() => {
        setTitleDraft(chapter?.title || '');
    }, [chapter?.id, chapter?.title]);

    // Refresh "Sauvegardé il y a Ns" every 10s
    useEffect(() => {
        if (saveState !== 'saved' || !lastSavedAt) return;
        const t = setInterval(() => setTick(x => x + 1), 10000);
        return () => clearInterval(t);
    }, [saveState, lastSavedAt]);

    useEffect(() => {
        if (!showStatusMenu) return;
        const h = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setShowStatusMenu(false); };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, [showStatusMenu]);

    useEffect(() => {
        if (!showExportMenu) return;
        const h = (e) => {
            if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
                setShowExportMenu(false);
                setExportSubmenu(null);
            }
        };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, [showExportMenu]);

    if (!chapter) return null;
    const status = STATUS_META[chapter.status] || STATUS_META.draft;
    const savedAgo = lastSavedAt ? formatAgo(lastSavedAt, tick) : '';
    const saveInfo = SAVE_STATES[saveState] || SAVE_STATES.idle;

    const commitRename = () => {
        const t = titleDraft.trim();
        if (t && t !== chapter.title && onRenameChapter) onRenameChapter(t);
        setEditingTitle(false);
    };

    const wordCount = chapter.word_count ?? 0;

    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
            borderBottom: '1px solid var(--card-border)',
            background: 'var(--card-bg)',
            minHeight: 56,
        }}>
            {/* Position */}
            <div style={{
                width: 32, height: 32, borderRadius: 8,
                background: 'rgba(139,92,246,0.12)', color: '#a78bfa',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, fontWeight: 600, flexShrink: 0,
            }}>
                {chapter.position ?? '·'}
            </div>

            {/* Titre éditable */}
            <div style={{ flex: 1, minWidth: 0 }}>
                {editingTitle ? (
                    <input
                        autoFocus
                        value={titleDraft}
                        onChange={(e) => setTitleDraft(e.target.value)}
                        onBlur={commitRename}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); commitRename(); }
                            else if (e.key === 'Escape') { setTitleDraft(chapter.title); setEditingTitle(false); }
                        }}
                        style={{
                            width: '100%', fontSize: 16, fontWeight: 600,
                            background: 'rgba(255,255,255,0.04)',
                            border: '1px solid rgba(139,92,246,0.3)',
                            borderRadius: 6, padding: '6px 10px',
                            color: 'var(--text)',
                        }}
                    />
                ) : (
                    <div
                        onClick={() => setEditingTitle(true)}
                        style={{
                            fontSize: 16, fontWeight: 600,
                            color: 'var(--text)',
                            cursor: 'text', padding: '6px 0',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                        title="Cliquer pour renommer"
                    >
                        {chapter.title || 'Sans titre'}
                    </div>
                )}
            </div>

            {/* Indicateur sauvegarde */}
            {(saveState !== 'idle' || savedAgo) && (
                <div style={{
                    fontSize: 11, color: saveInfo.color || '#8888a0',
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '4px 10px', borderRadius: 12,
                    background: 'rgba(255,255,255,0.04)',
                    flexShrink: 0,
                }}>
                    {saveState === 'saving' && <Clock size={12} />}
                    {saveState === 'saved' && <Check size={12} />}
                    <span>{saveState === 'saved' && savedAgo ? `Sauvegardé ${savedAgo}` : saveInfo.label}</span>
                </div>
            )}

            {/* Word count */}
            <div style={{
                fontSize: 12, color: 'var(--text-secondary)',
                padding: '4px 10px', borderRadius: 12,
                background: 'rgba(255,255,255,0.04)',
                flexShrink: 0,
            }}>
                {wordCount.toLocaleString('fr-FR')} mots
            </div>

            {/* Versions */}
            <button
                onClick={onOpenVersions}
                title="Historique des versions"
                style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    padding: '4px 10px', borderRadius: 12,
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid transparent',
                    color: '#a78bfa', fontSize: 12, cursor: 'pointer',
                    flexShrink: 0,
                }}
            >
                <History size={12} />
                v{versionCount}
            </button>

            {/* S58 — Notes stylistiques */}
            {onToggleNotes && (
                <button
                    onClick={onToggleNotes}
                    title={notesOpen ? 'Masquer les notes stylistiques' : 'Afficher les notes stylistiques'}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 4,
                        padding: '4px 10px', borderRadius: 12,
                        background: notesOpen ? 'rgba(245,158,11,0.15)' : 'rgba(255,255,255,0.04)',
                        border: `1px solid ${notesOpen ? 'rgba(245,158,11,0.35)' : 'transparent'}`,
                        color: notesOpen ? '#f59e0b' : 'var(--text-secondary)',
                        fontSize: 12, cursor: 'pointer', flexShrink: 0,
                    }}
                >
                    <Zap size={12} />
                    Notes · {notesCount}
                </button>
            )}

            {/* Statut dropdown */}
            <div ref={menuRef} style={{ position: 'relative', flexShrink: 0 }}>
                <button
                    onClick={() => setShowStatusMenu(v => !v)}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '5px 10px', borderRadius: 12,
                        background: 'rgba(255,255,255,0.04)',
                        border: `1px solid ${status.color}33`,
                        color: status.color, fontSize: 12, fontWeight: 500, cursor: 'pointer',
                    }}
                >
                    <CircleDot size={10} style={{ color: status.dot }} />
                    {status.label}
                </button>
                {showStatusMenu && (
                    <div style={{
                        position: 'absolute', top: '100%', right: 0, marginTop: 6,
                        background: 'var(--card-bg)',
                        border: '1px solid var(--card-border)',
                        borderRadius: 8, padding: 4, minWidth: 160,
                        boxShadow: '0 10px 30px rgba(0,0,0,0.4)', zIndex: 30,
                    }}>
                        {Object.entries(STATUS_META).map(([key, meta]) => (
                            <button
                                key={key}
                                onClick={() => { onStatusChange?.(key); setShowStatusMenu(false); }}
                                style={{
                                    width: '100%', textAlign: 'left',
                                    display: 'flex', alignItems: 'center', gap: 8,
                                    padding: '6px 10px', borderRadius: 6,
                                    background: key === chapter.status ? 'rgba(139,92,246,0.1)' : 'transparent',
                                    border: 'none', color: meta.color,
                                    fontSize: 12, cursor: 'pointer',
                                }}
                            >
                                <CircleDot size={10} style={{ color: meta.dot }} />
                                {meta.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* S57 — Bouton Réviser (work_chapter) */}
            <button
                onClick={onRevise}
                disabled={!onRevise}
                title="Réviser le chapitre avec l'IA (6 modes : style, densité, réécrire section…)"
                style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    padding: '5px 10px', borderRadius: 8,
                    background: 'rgba(139,92,246,0.12)',
                    border: '1px solid rgba(139,92,246,0.25)',
                    color: '#c4b5fd', fontSize: 12, fontWeight: 500,
                    cursor: onRevise ? 'pointer' : 'not-allowed',
                    opacity: onRevise ? 1 : 0.4, flexShrink: 0,
                }}
            >
                <Wand2 size={12} />
                Réviser
            </button>

            {/* S58 — Bouton Transition vers chapitre suivant (write_transition) */}
            <button
                onClick={onOpenTransitionToNext}
                disabled={!onOpenTransitionToNext || !hasNextChapter}
                title={hasNextChapter
                    ? 'Soigner la transition vers le chapitre suivant'
                    : 'Aucun chapitre suivant — ajoute-en un pour travailler la transition'}
                style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    padding: '5px 10px', borderRadius: 8,
                    background: 'rgba(139,92,246,0.08)',
                    border: '1px solid rgba(139,92,246,0.2)',
                    color: '#a78bfa', fontSize: 12, fontWeight: 500,
                    cursor: (onOpenTransitionToNext && hasNextChapter) ? 'pointer' : 'not-allowed',
                    opacity: (onOpenTransitionToNext && hasNextChapter) ? 1 : 0.4, flexShrink: 0,
                }}
            >
                <ArrowLeftRight size={12} />
                Transition
            </button>

            {/* Actions stubs S59 */}
            <button
                onClick={onConsolidate}
                disabled={!onConsolidate}
                title={onConsolidate ? 'Consolider le chapitre' : 'Consolidation livrée en S59'}
                style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    padding: '5px 10px', borderRadius: 8,
                    background: 'rgba(139,92,246,0.08)',
                    border: '1px solid rgba(139,92,246,0.18)',
                    color: '#a78bfa', fontSize: 12,
                    cursor: onConsolidate ? 'pointer' : 'not-allowed',
                    opacity: onConsolidate ? 1 : 0.4, flexShrink: 0,
                }}
            >
                <Sparkles size={12} />
                Consolider
            </button>
            <div ref={exportMenuRef} style={{ position: 'relative', flexShrink: 0 }}>
                <button
                    onClick={() => setShowExportMenu(v => !v)}
                    disabled={!bookId || !chapter?.id}
                    title="Exporter le chapitre (Markdown, Word ou PDF)"
                    style={{
                        display: 'flex', alignItems: 'center', gap: 4,
                        padding: '5px 10px', borderRadius: 8,
                        background: 'rgba(255,255,255,0.04)',
                        border: '1px solid var(--card-border)',
                        color: 'var(--text)', fontSize: 12,
                        cursor: bookId && chapter?.id ? 'pointer' : 'not-allowed',
                        opacity: bookId && chapter?.id ? 1 : 0.5, flexShrink: 0,
                    }}
                >
                    <DownloadIcon size={12} />
                    Export
                    <ChevronDown size={10} />
                </button>
                {showExportMenu && (
                    <div style={{
                        position: 'absolute', top: '100%', right: 0, marginTop: 6,
                        background: 'var(--card-bg)',
                        border: '1px solid var(--card-border)',
                        borderRadius: 8, padding: 4, minWidth: 220,
                        boxShadow: '0 10px 30px rgba(0,0,0,0.4)', zIndex: 30,
                    }}>
                        {exportSubmenu === null && (
                            <>
                                <button onClick={() => exportChapter('md')} style={expBtn}>
                                    <FileText size={12} /> Markdown (.md)
                                </button>
                                <button
                                    onClick={() => setExportSubmenu('docx')}
                                    style={{ ...expBtn, justifyContent: 'space-between' }}
                                >
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <FileType2 size={12} /> Word (.docx)
                                    </span>
                                    <ChevronDown size={10} style={{ transform: 'rotate(-90deg)' }} />
                                </button>
                                <button
                                    onClick={() => setExportSubmenu('pdf')}
                                    style={{ ...expBtn, justifyContent: 'space-between' }}
                                >
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <File size={12} /> PDF (.pdf)
                                    </span>
                                    <ChevronDown size={10} style={{ transform: 'rotate(-90deg)' }} />
                                </button>
                            </>
                        )}
                        {(exportSubmenu === 'docx' || exportSubmenu === 'pdf') && (
                            <>
                                <button
                                    onClick={() => setExportSubmenu(null)}
                                    style={{ ...expBtn, color: 'var(--text-muted)', fontSize: 10, padding: '4px 10px' }}
                                >
                                    ← Retour
                                </button>
                                <div style={{ height: 1, background: 'var(--card-border)', margin: '2px 0' }} />
                                <div style={{
                                    fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
                                    letterSpacing: 0.4, color: 'var(--text-muted)',
                                    padding: '6px 10px 2px',
                                }}>
                                    Palette
                                </div>
                                <button onClick={() => exportChapter(exportSubmenu, 'corporate')} style={expBtn}>
                                    <span style={paletteDot('#1D4ED8')} /> Corporate
                                    <span style={paletteHint}>bleu nuit · Inter</span>
                                </button>
                                <button onClick={() => exportChapter(exportSubmenu, 'editorial')} style={expBtn}>
                                    <span style={paletteDot('#9C7A3F')} /> Éditorial
                                    <span style={paletteHint}>crème · serif</span>
                                </button>
                                <button onClick={() => exportChapter(exportSubmenu, 'recherche')} style={expBtn}>
                                    <span style={paletteDot('#4B5563')} /> Recherche
                                    <span style={paletteHint}>neutre · académique</span>
                                </button>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const expBtn = {
    width: '100%', textAlign: 'left',
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '7px 10px', borderRadius: 6,
    background: 'transparent', border: 'none',
    color: 'var(--text)', fontSize: 12, cursor: 'pointer',
};

const paletteDot = (color) => ({
    width: 10, height: 10, borderRadius: 5,
    background: color, flexShrink: 0, display: 'inline-block',
});

const paletteHint = {
    marginLeft: 'auto',
    fontSize: 10, color: 'var(--text-muted)', fontStyle: 'italic',
};

function formatAgo(ts, _tick) {
    if (!ts) return '';
    const d = Date.now() - new Date(ts).getTime();
    if (d < 5000) return 'à l\'instant';
    const sec = Math.floor(d / 1000);
    if (sec < 60) return `il y a ${sec}s`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `il y a ${min} min`;
    const h = Math.floor(min / 60);
    if (h < 24) return `il y a ${h} h`;
    return `il y a ${Math.floor(h / 24)} j`;
}

export default ChapterHeader;
